import React, { useEffect } from 'react';
import { DollarSign, Calculator, Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import SalarySimulator from './SalarySimulator';

const formatCurrency = value => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = dateString => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If invalid, try to parse as date string (YYYY-MM-DD)
      const parts = dateString.split('T')[0].split('-');
      if (parts.length === 3) {
        return new Intl.DateTimeFormat('id-ID', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date(parts[0], parts[1] - 1, parts[2]));
      }
      return dateString;
    }

    return new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    // Fallback: if date string is in YYYY-MM-DD format, format it manually
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parts = dateString.split('T')[0].split('-');
      const months = [
        'Januari',
        'Februari',
        'Maret',
        'April',
        'Mei',
        'Juni',
        'Juli',
        'Agustus',
        'September',
        'Oktober',
        'November',
        'Desember',
      ];
      return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    }
    return dateString;
  }
};

export default function SalaryTab({
  salaryGrades: _salaryGrades,
  jobGrades: _jobGrades,
  salaryPolicies,
  positionMappings: _positionMappings,
  positions,
  departments: _departments,
  subDivisions: _subDivisions,
  onAddSalaryGrade: _onAddSalaryGrade,
  onEditSalaryGrade: _onEditSalaryGrade,
  onDeleteSalaryGrade: _onDeleteSalaryGrade,
  onAddJobGrade: _onAddJobGrade,
  onEditJobGrade: _onEditJobGrade,
  onDeleteJobGrade: _onDeleteJobGrade,
  onAddPolicy,
  onEditPolicy,
  onDeletePolicy,
  onAddMapping: _onAddMapping,
  onDeleteMapping: _onDeleteMapping,
  onReload: _onReload,
}) {
  // Debug: Log salaryPolicies
  useEffect(() => {
    // Salary policies updated
  }, [salaryPolicies]);

  return (
    <div className="space-y-4">
      {/* Alert message */}
      <Alert className="border-yellow-200 bg-yellow-50 rounded-lg flex items-center gap-3">
        <AlertCircle className="h-4 w-4 text-orange-600 flex-shrink-0" />
        <AlertDescription className="text-orange-700">
          Perubahan kebijakan gaji tidak mempengaruhi payroll historis. Gaji dihitung otomatis
          berdasarkan Jabatan, Masa Kerja, dan Level Kompetensi.
        </AlertDescription>
      </Alert>

      {/* Policy section */}
      <div>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Policy List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calculator className="w-5 h-5" />
                  Daftar Kebijakan
                </CardTitle>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onAddPolicy()}
                >
                  <Plus className="w-4 h-4 mr-2" /> Tambah
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Alert className="border-blue-200 bg-blue-50 mb-4 flex items-center gap-3">
                <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <AlertDescription className="text-blue-800 text-xs">
                  Gaji otomatis dihitung: Jabatan → Level → Mid Salary + (Masa Kerja × Kenaikan) +
                  (Kompetensi × Faktor)
                </AlertDescription>
              </Alert>

              {!salaryPolicies || salaryPolicies.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Belum ada kebijakan gaji</p>
                  <p className="text-sm text-gray-400 mt-1">Buat kebijakan pertama</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {salaryPolicies.map(item => {
                    return (
                      <div
                        key={item.id}
                        className={`p-4 rounded-lg border-2 ${item.is_active === true || item.is_active === 'true' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{item.policy_name}</h3>
                              {(item.is_active === true || item.is_active === 'true') && (
                                <Badge className="bg-emerald-600">Aktif</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">{item.policy_code}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => onEditPolicy(item)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onDeletePolicy(item.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm mt-3">
                          <div>
                            <p className="text-gray-500 text-xs">Base Salary</p>
                            <p className="font-semibold">{formatCurrency(item.base_salary)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Top Salary</p>
                            <p className="font-semibold">{formatCurrency(item.top_salary)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Kenaikan/Tahun</p>
                            <p className="font-semibold">
                              {item.yearly_increment
                                ? formatCurrency(item.yearly_increment)
                                : item.tenure_increment_percent
                                  ? `${item.tenure_increment_percent}%`
                                  : '0'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs">Faktor Kompetensi</p>
                            <p className="font-semibold">
                              {item.competency_factor
                                ? formatCurrency(item.competency_factor)
                                : item.competency_multiplier
                                  ? 'Multiplier'
                                  : '0'}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3 text-xs">
                          {(item.use_tenure_adjustment === true ||
                            item.use_tenure_adjustment === 'true' ||
                            item.toggle_tenure) && (
                            <Badge variant="outline" className="bg-blue-50">
                              Masa Kerja ✓
                            </Badge>
                          )}
                          {(item.use_competency_adjustment === true ||
                            item.use_competency_adjustment === 'true' ||
                            item.toggle_competency) && (
                            <Badge variant="outline" className="bg-purple-50">
                              Kompetensi ✓
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Berlaku: {formatDate(item.effective_date)}{' '}
                          {item.end_date && `- ${formatDate(item.end_date)}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Salary Simulator */}
          <SalarySimulator
            positions={positions}
            salaryPolicies={salaryPolicies}
            formatCurrency={formatCurrency}
          />
        </div>
      </div>
    </div>
  );
}
